/**
 * One WHO HAS ACCESS row (PRD-6.1, SE-8313): avatar, name and role. The owner
 * row is locked to "Owner"; every other row carries a `Can edit` / `Can view`
 * Select and a revoke action, both disabled while a write is in flight.
 * SE-8359 (PV17): a person outranked by the "Everyone at …" group carries a
 * caption saying so (R06 §3); an INACTIVE member keeps the row with an
 * "Inactive" pill and no role control (R06 §5) — revoke stays, so the owner
 * can still tidy the grant away.
 * @module features/scouting/components/molecules/ShareAccessRow/ShareAccessRowView
 */

import { ChevronDownIcon, XIcon } from "lucide-react-native";
import { memo } from "react";

import { Avatar, AvatarFallbackText } from "@/components/atoms/Avatar";
import { TonePill } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from "@/components/atoms/Select";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_WATCHLIST_SHARE } from "e2e/testId";

/** One selectable role. */
export interface ShareAccessRoleOption {
  /** Select value (a grantable role). */
  readonly value: string;
  /** Visible label. */
  readonly label: string;
}

/** Props for {@link ShareAccessRowView}. */
export interface ShareAccessRowViewProps {
  /** User id (test identity). */
  readonly userId: string;
  /** Display name. */
  readonly name: string;
  /** Current role as a Select value. */
  readonly role: string;
  /** Role label under the name ("Owner", "Editor", "Viewer"). */
  readonly roleLabel: string;
  /** Label of the selected role option ("Can edit" / "Can view"). */
  readonly roleOptionLabel: string;
  /** Whether this is the locked owner row. */
  readonly isOwner: boolean;
  /** Whether a role change or revoke is in flight. */
  readonly isPending: boolean;
  /** Effective-role caption under the role label, or null (SE-8359). */
  readonly caption?: string | null;
  /** Membership not ACTIVE: "Inactive" pill, no role control (SE-8359). */
  readonly isInactive?: boolean;
  /** Selectable roles. */
  readonly roleOptions: readonly ShareAccessRoleOption[];
  /** Role Select change handler. */
  readonly onRoleChange: (value: string) => void;
  /** Revoke handler. */
  readonly onRevoke: () => void;
}

/**
 * Renders one access row.
 * @param root0 - View props
 * @param root0.userId - User id
 * @param root0.name - Display name
 * @param root0.role - Current role value
 * @param root0.roleLabel - Role label under the name
 * @param root0.roleOptionLabel - Selected option label
 * @param root0.isOwner - Locked owner row
 * @param root0.isPending - Write in flight
 * @param root0.caption - Effective-role caption, or null
 * @param root0.isInactive - Membership not ACTIVE
 * @param root0.roleOptions - Selectable roles
 * @param root0.onRoleChange - Role change handler
 * @param root0.onRevoke - Revoke handler
 * @returns The rendered row.
 */
const ShareAccessRowView = ({
  userId,
  name,
  role,
  roleLabel,
  roleOptionLabel,
  isOwner,
  isPending,
  caption = null,
  isInactive = false,
  roleOptions,
  onRoleChange,
  onRevoke,
}: ShareAccessRowViewProps) => (
  <Stack
    direction="horizontal"
    testID={TEST_ID_WATCHLIST_SHARE.accessRowFor(userId)}
    className="items-center gap-3"
  >
    {/* molecules/ShareAccessRow (Figma 970:19119): accent avatar with the
        initials, and "Owner" on the right of the owner's line. */}
    <Avatar size="sm" className="bg-accent-primary">
      <AvatarFallbackText>{name}</AvatarFallbackText>
    </Avatar>
    <Stack className="min-w-0 flex-1">
      <Text
        variant="body-strong"
        className="text-content-primary"
        numberOfLines={1}
      >
        {name}
      </Text>
      {/* The owner's role reads on the right (Figma); others keep theirs. */}
      {isOwner ? null : (
        <Box testID={TEST_ID_WATCHLIST_SHARE.roleLabelFor(userId)}>
          <Text variant="caption" className="text-content-muted">
            {roleLabel}
          </Text>
        </Box>
      )}
      {caption ? (
        <Box testID={TEST_ID_WATCHLIST_SHARE.captionFor(userId)}>
          <Text variant="micro" className="text-content-subtle">
            {caption}
          </Text>
        </Box>
      ) : null}
    </Stack>
    {isInactive ? (
      <TonePill
        testID={TEST_ID_WATCHLIST_SHARE.inactiveFor(userId)}
        tone="neutral-subtle"
        label="Inactive"
        textVariant="micro"
        className="px-2 py-micro"
      />
    ) : null}
    {isOwner ? (
      <Text variant="caption" className="text-content-muted">
        Owner
      </Text>
    ) : null}
    {isOwner || isInactive ? null : (
      <Select
        selectedValue={role}
        onValueChange={onRoleChange}
        isDisabled={isPending}
      >
        <SelectTrigger
          testID={TEST_ID_WATCHLIST_SHARE.roleSelectFor(userId)}
          accessibilityLabel={`Change ${name}'s role`}
          variant="outline"
          size="sm"
          className="h-9 rounded-sm border-outline-default px-3"
        >
          <SelectInput
            value={roleOptionLabel}
            placeholder="Role"
            className="text-body"
          />
          <SelectIcon as={ChevronDownIcon} className="text-content-muted" />
        </SelectTrigger>
        {/* useRNModal: same reason as ShareInviteRowView — inside the mobile
            share sheet's RN Modal the root-overlay Select sheet is unreachable. */}
        <SelectPortal useRNModal>
          <SelectBackdrop />
          <SelectContent>
            <SelectScrollView>
              {roleOptions.map(option => (
                <SelectItem
                  key={option.value}
                  label={option.label}
                  value={option.value}
                />
              ))}
            </SelectScrollView>
          </SelectContent>
        </SelectPortal>
      </Select>
    )}
    {isOwner ? null : (
      <Pressable
        testID={TEST_ID_WATCHLIST_SHARE.revokeFor(userId)}
        onPress={onRevoke}
        disabled={isPending}
        accessibilityRole="button"
        accessibilityLabel={`Remove ${name}`}
        className="web:hover:bg-surface-raised size-9 items-center justify-center rounded-sm border border-outline-default"
      >
        <Icon as={XIcon} size="sm" className="text-content-secondary" />
      </Pressable>
    )}
  </Stack>
);

ShareAccessRowView.displayName = "ShareAccessRowView";

export default memo(ShareAccessRowView);
